/**
 * The landing screen.
 *
 * It is deliberately thin, and it says so. Everything real lives inside a
 * client's workspace (P7) — this screen's one job is the door to the client
 * list, and a dashboard full of invented tiles would be a lie about where the
 * work happens.
 */

import { ArrowRight, Building2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link } from 'react-router'

import { CLIENT, useEvaluation } from '../lib/access'
import { clients } from '../lib/api'

export function Overview() {
  const { can } = useEvaluation()
  const [count, setCount] = useState<number | null>(null)

  useEffect(() => {
    if (!can(CLIENT, 'can_view')) return
    clients
      .list({ limit: 1 })
      .then((body) => setCount(Number(body.meta?.total ?? 0)))
      .catch(() => setCount(null))
  }, [can])

  return (
    <div>
      <h1 className="mb-1 text-xl font-semibold text-foreground">TDS Reco</h1>
      <p className="mb-8 text-sm text-muted-foreground">
        Form 26AS against the books, reconciled — for every taxpayer your practice acts for.
      </p>

      {can(CLIENT, 'can_view') && (
        <Link
          to="/clients"
          className="flex max-w-sm items-center gap-4 rounded-2xl border border-border bg-surface-raised p-5 transition-colors hover:border-primary/40"
        >
          <Building2 className="text-primary" size={22} />
          <span className="flex-1">
            <span className="block text-sm font-medium text-foreground">Clients</span>
            <span className="block text-xs text-muted-foreground">
              {count === null ? 'Taxpayers you reconcile for' : `${count} on your list`}
            </span>
          </span>
          <ArrowRight size={16} className="text-muted-foreground" />
        </Link>
      )}

      {/* The real dashboard (open runs, tie-out status, review queue depth)
          arrives with its phases (P3–P5). Left as an honest statement rather
          than as placeholder tiles: a fake number on a dashboard is believed. */}
      <p className="mt-10 max-w-lg text-xs leading-relaxed text-muted-foreground">
        Uploads, runs and the reconciliation workspace are in development. This build carries the
        platform foundation — sign-in, organisations, permissions, client records, the audit trail
        and document storage.
      </p>
    </div>
  )
}
